import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { BlindIcon, DownArrow } from 'icons';
import { categories } from 'utils';
import { useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'framer-motion';

type UnifiedMediaItem = {
  title: string;
  description1?: string;
  description2?: string;
  description3?: string;
  detail1?: string;
  detail2?: string;
  detail3?: string;
  src?: string;
  src2?: string;
  _rawTitleKey?: string;
  [key: string]: any;
};

const UnifiedCard: React.FC<{
  item: UnifiedMediaItem;
  selectedCategory: string;
  onDetails: () => void;
  tallMobile?: boolean; // ✅ NEW
}> = ({ item, selectedCategory, onDetails, tallMobile = false }) => {
  const imgSrc = item.src || item.src2;

  const isExternal = selectedCategory === 'External Blinds';
  const isZip = selectedCategory === 'Zip Screen';
  const isSelfBearing = selectedCategory === 'Self-Bearing Systems';
  const isFacadeSystem = selectedCategory === 'Facade Systems';

  const imagePositionClass = isExternal
    ? 'items-center justify-end h-full'
    : isZip || isSelfBearing
      ? 'items-end justify-end'
      : 'items-center justify-center';

  const imageSizeClass = isExternal
    ? 'h-full w-auto object-contain'
    : selectedCategory === 'Shading Systems & Umbrellas'
      ? 'h-full w-full object-cover object-left'
      : 'max-w-full h-auto object-contain';

  const { t } = useTranslation();

  return (
    <div
      className={`w-full bg-[#f7f7f7] text-black rounded-xl shadow-xl overflow-hidden grid grid-cols-1 lg:grid-cols-2 min-h-[600px] md:min-h-[500px] lg:min-h-[400px] mb-0 md:mb-10 ${
        selectedCategory === 'Facade Systems' ||
        selectedCategory === 'Self-Bearing Systems'
          ? 'max-h-[700px] md:max-h-[600px] lg:max-h-[500px]'
          : 'max-h-[600px] md:max-h-[500px] lg:max-h-[400px]'
      }`}
    >
      {/* Left: Content */}
      <div className={`p-5 sm:p-8 flex flex-col justify-between min-h-0`}>
        <div className="flex-1 min-h-0 flex flex-col">
          <h3
            className={`${selectedCategory === 'Shading Systems & Umbrellas' ? 'text-[28px]' : 'text-[40px]'} font-normal leading-none`}
          >
            {item.title}
          </h3>

          <div
            className={`mt-3 pr-1 overflow-auto flex-1 ${
              selectedCategory === 'Facade Systems' ||
              selectedCategory === 'Self-Bearing Systems'
                ? 'max-h-[200px] md:max-h-[180px] lg:max-h-[160px]'
                : 'md:max-h-[245px]'
            }`}
          >
            {isFacadeSystem ? (
              <>
                {item.detail1 && <p className="mt-2">{item.detail1}</p>}
                {item.detail2 && <p className="mt-2">{item.detail2}</p>}
                {item.detail3 && <p className="mt-2">{item.detail3}</p>}
              </>
            ) : (
              <>
                {item.description1 && (
                  <p className="mt-2">{item.description1}</p>
                )}
                {item.description2 && (
                  <p className="mt-2">{item.description2}</p>
                )}
                {item.description3 && (
                  <p className="mt-2">{item.description3}</p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="pt-4 mt-auto">
          <button
            type="button"
            onClick={onDetails}
            className="p-0 w-fit text-[#1E90AA] hover:text-[#1E90AA]/80 transition-colors"
          >
            {t('card.detailsButton')}
          </button>
        </div>
      </div>

      {/* Right: Image (NO padding) */}
      <div className={`relative flex ${imagePositionClass} bg-transparent`}>
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={item.title}
            className={imageSizeClass}
            style={{ maxHeight: tallMobile ? 600 : 500 }}
          />
        ) : null}
        {selectedCategory === 'Shading Systems & Umbrellas' && (
          <div className="absolute inset-0 bg-gradient-to-b from-[#F7F7F7] to-transparent pointer-events-none h-[50%] -top-[1px] md:top-0 md:-left-[1px] md:bg-gradient-to-r md:h-full md:w-[50%]" />
        )}
      </div>
    </div>
  );
};

export const Section3 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] =
    useState<string>('External Blinds');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const media = categories[selectedCategory] || [];

  const translatedMedia = useMemo(() => {
    return media.map((item: any) => ({
      ...item,
      _rawTitleKey: item.title,
      title: t(`section3.categories.${selectedCategory}.${item.title}.title`),
      description1: t(
        `section3.categories.${selectedCategory}.${item.title}.description1`,
      ),
      description2: t(
        `section3.categories.${selectedCategory}.${item.title}.description2`,
      ),
      description3: t(
        `section3.categories.${selectedCategory}.${item.title}.description3`,
      ),
      detail1: t(
        `section3.categories.${selectedCategory}.${item.title}.detail1`,
      ),
      detail2: t(
        `section3.categories.${selectedCategory}.${item.title}.detail2`,
      ),
      detail3: t(
        `section3.categories.${selectedCategory}.${item.title}.detail3`,
      ),
    }));
  }, [media, selectedCategory, t]);

  const handleDetails = (item: any) => {
    const categorySegment = encodeURIComponent(selectedCategory);
    const productSegment = encodeURIComponent(item._rawTitleKey || item.title);
    navigate(`/product-details/${categorySegment}/${productSegment}`);
  };

  // ✅ NEW: single-center mode for these categories
  const isSingleCenterCategory =
    selectedCategory === 'Insect Screens' ||
    selectedCategory === 'Facade Systems';

  // ✅ If only one item OR forced single-center category -> no carousel
  const shouldUseCarousel =
    !isSingleCenterCategory && translatedMedia.length > 1;

  // -----------------------------
  // Embla (only loops when carousel is active)
  // -----------------------------
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: shouldUseCarousel,
    align: 'center',
    skipSnaps: false,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.scrollTo(0, true);
  }, [selectedCategory, emblaApi]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  return (
    <>
      <Helmet>
        <title>Products & Categories — Sonnen Raffstoren</title>
        <meta
          name="description"
          content="Explore product categories from Sonnen Raffstoren GmbH in Switzerland, including external blinds, zip screens, self-bearing systems and façade solutions. Compare models and design options to select the right shading system for residential or commercial projects."
        />
      </Helmet>

      <section className="md:px-mediumPx lg:px-largePx xl:px-[200px] py-[32px] md:py-mediumPy lg:py-largePy bg-gradient-to-b from-[#1E90AA] to-[#0C3A44] lg:min-h-[600px]">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Category dropdown */}
          <div className="relative inline-block mb-6 px-smallPx">
            <button
              className="flex flex-row gap-2 items-center text-[24px] text-white"
              onClick={() => setDropdownOpen((v) => !v)}
            >
              <BlindIcon />
              <h1>{t(`section3.categories.${selectedCategory}.name`)}</h1>
              <DownArrow />
            </button>

            {dropdownOpen && (
              <ul className="absolute bg-white text-black w-full mt-2 rounded shadow-md z-10">
                {Object.keys(categories).map((category) => (
                  <li
                    key={category}
                    className="px-4 py-2 hover:bg-gray-200 cursor-pointer"
                    onClick={() => {
                      setSelectedCategory(category);
                      setDropdownOpen(false);
                    }}
                  >
                    {t(`section3.categories.${category}.name`)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ✅ Single card (center) */}
          {!shouldUseCarousel && translatedMedia[0] && (
            <div className="px-smallPx">
              <div className="mx-auto max-w-[1100px] flex justify-center">
                <div className="w-full">
                  <UnifiedCard
                    item={translatedMedia[0]}
                    selectedCategory={selectedCategory}
                    onDetails={() => handleDetails(translatedMedia[0])}
                    tallMobile={isSingleCenterCategory} // ✅ 600px on mobile for insect/facade
                  />
                </div>
              </div>
            </div>
          )}

          {/* ✅ Carousel (unchanged behavior) */}
          {shouldUseCarousel && (
            <div className="px-0 md:px-0">
              <div className="mx-auto">
                <div className="relative">
                  <button
                    type="button"
                    onClick={scrollPrev}
                    className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 h-10 w-10 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white backdrop-blur"
                    aria-label="Previous"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={scrollNext}
                    className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 h-10 w-10 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white backdrop-blur"
                    aria-label="Next"
                  >
                    ›
                  </button>

                  <div ref={emblaRef} className="overflow-hidden">
                    <div className="flex touch-pan-y">
                      {translatedMedia.map((item: any, idx: number) => {
                        const isCenter = idx === selectedIndex;

                        const blur = isCenter ? 0 : 3;
                        const scale = isCenter ? 1 : 0.92;
                        const opacity = isCenter ? 1 : 0.3;

                        return (
                          <div
                            key={`${selectedCategory}-${idx}`}
                            className="flex-[0_0_85%] md:flex-[0_0_70%] lg:flex-[0_0_60%] px-3 md:px-4"
                            style={{
                              transform: `scale(${scale})`,
                              opacity,
                              filter: `blur(${blur}px)`,
                              transition:
                                'transform 300ms ease, opacity 300ms ease, filter 300ms ease',
                            }}
                          >
                            <UnifiedCard
                              item={item}
                              selectedCategory={selectedCategory}
                              onDetails={() => handleDetails(item)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </section>
    </>
  );
};

export default Section3;
